import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';

/**
 * useSplashScreen Hook
 * 
 * Manages native splash screen behavior on mobile platforms.
 * Hides the native splash immediately so the in-app video splash (local MP4) can show.
 * 
 * Flow:
 * 1. Native splash shows briefly (should be nearly instant with launchShowDuration: 0)
 * 2. React app starts loading
 * 3. Hide native splash after a very short delay so the WebView is ready for <video>
 * 4. AnimatedSplash shows bundled splash-animation.mp4 (Vite /assets) until it ends, then fades out
 */
export function useSplashScreen() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    let hasHidden = false;

    const hideSplash = async () => {
      // Prevent double-hide
      if (hasHidden) return;
      
      try {
        await SplashScreen.hide();
        hasHidden = true;
        
        // Log only in development
        if (import.meta.env.DEV) {
          console.log('[SplashScreen] Native splash hidden - AnimatedSplash taking over');
        }
      } catch (error) {
        // Only log errors in development
        if (import.meta.env.DEV) {
          console.error('[SplashScreen] Error hiding:', error);
        }
      }
    };

    // Short delay + next frame so the document and first layout pass are ready for <video> paint
    const t = window.setTimeout(() => {
      requestAnimationFrame(() => {
        void hideSplash();
      });
    }, 150);

    return () => {
      window.clearTimeout(t);
      if (!hasHidden) {
        SplashScreen.hide().catch(() => {});
      }
    };
  }, []);
}

